import { Fragment } from 'react'
import { useParams } from 'react-router'

import { ROUTE_UI_SECTION_NAME } from '../../../constants/route'
import {
  useIsOragClusterDisplayed,
  useSectionPermissions,
  useUserData,
} from '../../../hooks'
import { isStaffUser } from '../../../utils/user'
import { EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE } from '../../Email'
import { useUserProfileContext } from '../../User'
import { useUserProfilePageCreateEmailAction } from '../UserProfilePageHeaderFooter'

import {
  USER_PROFILE_PAGE_INHABITANT_WIDGET_LIST_ORDER,
  USER_PROFILE_PAGE_STAFF_WIDGET_LIST_ORDER,
} from './UserProfilePageWidgetList.constants'
import {
  useUserProfilePageWidgetListInfo,
  useUserProfilePageWidgetListInfoOptions,
} from './UserProfilePageWidgetList.hooks'
import {
  getUserProfilePageWidgetListContactEmailType,
  getUserProfilePageWidgetListPermissions,
  getUserProfilePageWidgetListWidgetsByKey,
} from './UserProfilePageWidgetList.utils'

function UserProfilePageWidgetList({
  getContactsApi,
  getPermissions = getUserProfilePageWidgetListPermissions,
}) {
  const { inhabitantId } = useParams()
  const { isInitiated, userData } = useUserProfileContext()

  const currentUser = useUserData()
  const displayOragCluster = useIsOragClusterDisplayed()

  const { canCreate: canCreateEmails } = useSectionPermissions({
    sectionName: ROUTE_UI_SECTION_NAME.mails,
  })

  const { handleClickEmail } = useUserProfilePageCreateEmailAction()

  const isStaff = userData?.isStaff

  const {
    canEditContacts,
    canEditInfo,
    canSendEmail,
    canViewContacts,
    canViewInfo,
  } = getPermissions({ canCreateEmails, userData })
  const canViewCreated = isStaffUser(currentUser)

  const {
    info,
    isInitiated: isInfoInitiated,
    updateInfoBlock,
  } = useUserProfilePageWidgetListInfo({
    canView: !isStaff && canViewInfo,
    profileId: inhabitantId,
  })

  const options = useUserProfilePageWidgetListInfoOptions({
    canEdit: !isStaff && canViewInfo && canEditInfo,
  })

  function handleClickContactEmail({ type, value }) {
    handleClickEmail({
      email: value,
      type: getUserProfilePageWidgetListContactEmailType(type),
    })
  }

  function handleClickPartnerEmail(email) {
    handleClickEmail({
      email,
      type: EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE.partner,
    })
  }

  const isProfileLoading = !isInitiated || !userData
  const isInfoSkeleton = isProfileLoading || !isInfoInitiated || !info

  const widgetsByKey = getUserProfilePageWidgetListWidgetsByKey({
    canEditContacts,
    canEditInfo,
    canViewContacts,
    canViewCreated,
    canViewInfo,
    displayOragCluster,
    getContactsApi,
    info,
    isInfoSkeleton,
    isProfileLoading,
    options,
    profileId: inhabitantId,
    userData,
    onClickContactEmail: canSendEmail ? handleClickContactEmail : null,
    onClickPartnerEmail: canSendEmail ? handleClickPartnerEmail : null,
    onSubmitInfo: updateInfoBlock,
  })

  // Inhabitant is the loading default: both flavors start with the same
  // contacts skeleton, so there is no flash when staff data arrives.
  const widgetListOrder = isStaff
    ? USER_PROFILE_PAGE_STAFF_WIDGET_LIST_ORDER
    : USER_PROFILE_PAGE_INHABITANT_WIDGET_LIST_ORDER

  return widgetListOrder.map(widgetKey => {
    const widget = widgetsByKey[widgetKey]

    return widget ? <Fragment key={widgetKey}>{widget}</Fragment> : null
  })
}

export default UserProfilePageWidgetList
